// Quotes feature screen (task 3.6) — Req 4.2.
//
// Priced, itemised offers. Wired to the Phase 2 quotes handler:
//   GET  /quotes?status=<status>   list by status
//   GET  /quotes/{id}              detail
//   POST /quotes                   create ({ clientId, jobId?, lines })
//   PUT  /quotes/{id}              edit lines / move status
//
// Money is computed server-side (NFR6.2): the create/edit forms only submit line
// items; the totals shown on the detail screen come straight from the API record
// (subtotalPence / vatPence / totalPence). A new quote is always a draft and is
// never auto-sent — "sending" is the explicit draft→sent status button (Req 4.7).
// The quotes list has no "all" endpoint, so it queries each status and groups the
// results.

import { api } from '../api.js';
import {
  h, mount, runAsync, emptyView, routeToHash,
  screenEl, card, listRow, detailList, sectionHeading, muted,
  field, makeSelect, makeTextarea, errorSlot, setError,
  button, linkButton, buttonRow, runAction, goTo,
  statusBadge, shortDate, linesEditor, totalsBlock, moneyFromPence,
  QUOTE_TRANSITIONS, STATUS_ACTION_LABELS, packJobId,
  draftLinesEditor, draftLineToEditor, quoteDraftSummary,
  modeToggle, takeDraftSeed, aiUnavailableNotice,
} from './ui.js';
import { guidelineFlagsCard } from './guidelines.js';
import { quickbooksPushCard } from './quickbooks.js';
import { pdfDownloadCard } from './pdf.js';

const QUOTE_STATUSES = ['draft', 'sent', 'accepted', 'rejected', 'expired'];
const quoteHash = (id) => routeToHash({ section: 'quotes', action: 'view', id });

// --- List (grouped by status) ----------------------------------------------

export function quotesListScreen() {
  const body = h('div');

  const load = () =>
    runAsync(
      body,
      async () => {
        const results = await Promise.all(QUOTE_STATUSES.map((s) => api.get(`/quotes?status=${encodeURIComponent(s)}`)));
        return QUOTE_STATUSES.map((status, i) => ({ status, items: (results[i] && results[i].items) || [] })).filter((g) => g.items.length);
      },
      (groups) => renderGroups(groups),
      { loadingMessage: 'Loading quotes…' },
    );

  const renderGroups = (groups) => {
    if (!groups.length) {
      return emptyView({ message: 'No quotes yet.', actionLabel: 'New quote', onAction: () => goTo({ section: 'quotes', action: 'new' }) });
    }
    return h('div', {}, groups.map((g) =>
      h('div', { class: 'job-group' }, [
        h('div', { class: 'group-head' }, [statusBadge(g.status), h('span', { class: 'group-count', text: `${g.items.length}` })]),
        h('div', { class: 'list' }, g.items.map((q) =>
          listRow({
            href: quoteHash(q.id),
            title: q.ref,
            subtitle: shortDate(q.updatedAt),
            meta: h('span', { class: 'list-row-amount', text: moneyFromPence(q.totalPence) }),
          }),
        )),
      ]),
    ));
  };

  load();
  return screenEl('Quotes', body, {
    section: 'quotes',
    actions: linkButton('＋ New', routeToHash({ section: 'quotes', action: 'new' }), { variant: 'primary', small: true }),
  });
}

// --- Create ----------------------------------------------------------------

export function quoteNewScreen() {
  const body = h('div');

  runAsync(
    body,
    () => Promise.all([api.get('/clients'), api.get('/ratecard').catch(() => ({ items: [] }))]),
    ([clientsRes, rateRes]) => renderNew((clientsRes && clientsRes.items) || [], (rateRes && rateRes.items) || []),
    { loadingMessage: 'Loading…' },
  );

  // AI drafting is the headline flow (Req 4.1); a manual builder stays available.
  // A "Draft from this job" action elsewhere leaves a seed we pre-fill here.
  const renderNew = (clients, rateItems) => {
    if (!clients.length) {
      return emptyView({ message: 'Add a client before drafting a quote.', actionLabel: 'New client', onAction: () => goTo({ section: 'clients', action: 'new' }) });
    }
    const seed = takeDraftSeed('quotes');
    const holder = h('div');
    const toggle = modeToggle(
      [{ value: 'ai', label: '✨ AI draft' }, { value: 'manual', label: 'Manual' }],
      'ai',
      (mode) => mount(holder, mode === 'ai' ? aiForm() : manualForm()),
    );
    mount(holder, aiForm());
    return h('div', {}, [toggle, holder]);

    // --- AI draft form: plain-language description → structured draft --------
    function aiForm() {
      const clientSelect = makeSelect(
        [{ value: '', label: 'Select a client…' }, ...clients.map((c) => ({ value: c.id, label: c.name }))],
        { value: (seed && seed.clientId) || '' },
      );
      const jobField = h('div');
      const jobRef = { el: makeSelect([{ value: '', label: 'No linked job' }], { value: '' }) };
      mount(jobField, jobRef.el);

      const loadJobs = async (preselect) => {
        if (!clientSelect.value) {
          jobRef.el = makeSelect([{ value: '', label: 'No linked job' }], { value: '' });
          mount(jobField, jobRef.el);
          return;
        }
        jobRef.el = makeSelect([{ value: '', label: 'Loading jobs…' }], { value: '' });
        mount(jobField, jobRef.el);
        try {
          const res = await api.get(`/clients/${encodeURIComponent(clientSelect.value)}/jobs`);
          const jobs = (res && res.items) || [];
          const opts = [{ value: '', label: 'No linked job' }, ...jobs.map((j) => ({ value: j.id, label: `${truncate(j.description, 40)} (${j.status})` }))];
          jobRef.el = makeSelect(opts, { value: preselect || '' });
          mount(jobField, jobRef.el);
        } catch {
          jobRef.el = makeSelect([{ value: '', label: 'No linked job' }], { value: '' });
          mount(jobField, jobRef.el);
        }
      };
      clientSelect.addEventListener('change', () => loadJobs());

      const descInput = makeTextarea({ rows: 4, placeholder: 'e.g. Replace consumer unit and add a double socket in the kitchen…', value: (seed && seed.description) || '' });
      const err = errorSlot();
      const draftBtn = button('✨ Draft with AI', { variant: 'primary' });

      draftBtn.addEventListener('click', async () => {
        const clientId = clientSelect.value;
        const description = descInput.value.trim();
        if (!clientId) return setError(err, 'Please choose a client.');
        if (!description) return setError(err, 'Describe the work so the AI can draft a quote.');
        const payload = { clientId, description, jobId: jobRef.el.value || undefined };
        const result = await runAction(draftBtn, () => api.post('/quotes/draft', payload), { busyLabel: 'Drafting…', errSlot: err });
        if (result && result.quote) mount(body, renderDraftReview(result));
      });

      if (seed && seed.clientId) loadJobs(seed.jobId);

      return h('div', { class: 'form' }, [
        muted('Describe the job in plain language and the app drafts an itemised quote for you to review and edit. Nothing is sent — you confirm everything.'),
        field('Client', clientSelect),
        h('div', { class: 'field' }, [h('label', { class: 'field-label', text: 'Linked job (optional)' }), jobField]),
        field('What does the job involve?', descInput),
        err,
        buttonRow([draftBtn, linkButton('Cancel', routeToHash({ section: 'quotes' }))]),
      ]);
    }

    // --- Manual builder (unchanged behaviour) --------------------------------
    function manualForm() {
      const clientSelect = makeSelect([{ value: '', label: 'Select a client…' }, ...clients.map((c) => ({ value: c.id, label: c.name }))], { value: '' });
      const jobField = h('div');
      const jobRef = { el: makeSelect([{ value: '', label: 'No linked job' }], { value: '' }) };
      mount(jobField, jobRef.el);

      clientSelect.addEventListener('change', async () => {
        jobRef.el = makeSelect([{ value: '', label: 'Loading jobs…' }], { value: '' });
        mount(jobField, jobRef.el);
        if (!clientSelect.value) {
          jobRef.el = makeSelect([{ value: '', label: 'No linked job' }], { value: '' });
          mount(jobField, jobRef.el);
          return;
        }
        try {
          const res = await api.get(`/clients/${encodeURIComponent(clientSelect.value)}/jobs`);
          const jobs = (res && res.items) || [];
          const opts = [{ value: '', label: 'No linked job' }, ...jobs.map((j) => ({ value: j.id, label: `${truncate(j.description, 40)} (${j.status})` }))];
          jobRef.el = makeSelect(opts, { value: '' });
          mount(jobField, jobRef.el);
        } catch {
          jobRef.el = makeSelect([{ value: '', label: 'No linked job' }], { value: '' });
          mount(jobField, jobRef.el);
        }
      });

      const editor = linesEditor({ initial: [] });
      const rateAdder = rateCardAdder(rateItems, editor);
      const err = errorSlot();
      const submit = button('Create quote', { variant: 'primary' });

      submit.addEventListener('click', async () => {
        const clientId = clientSelect.value;
        const lines = editor.lines();
        if (!clientId) return setError(err, 'Please choose a client.');
        if (!lines.length) return setError(err, 'Add at least one line with a description.');
        const payload = { clientId, jobId: jobRef.el.value || null, lines };
        const created = await runAction(submit, () => api.post('/quotes', payload), { busyLabel: 'Creating…', errSlot: err });
        if (created && created.id) goTo(quoteHash(created.id));
      });

      return h('div', { class: 'form' }, [
        field('Client', clientSelect),
        h('div', { class: 'field' }, [h('label', { class: 'field-label', text: 'Linked job' }), jobField]),
        sectionHeading('Lines'),
        rateAdder,
        editor.el,
        err,
        buttonRow([submit, linkButton('Cancel', routeToHash({ section: 'quotes' }))]),
      ]);
    }

    // --- Editable review of the AI draft (Req 4.2, 4.7, NFR6.1) --------------
    function renderDraftReview(result) {
      const quote = result.quote;
      const editor = draftLinesEditor({ initial: (quote.lines || []).map(draftLineToEditor) });
      const err = errorSlot();
      const save = button('Save draft', { variant: 'primary' });
      const discard = button('Discard', {});
      discard.addEventListener('click', () => goTo({ section: 'quotes' }));
      save.addEventListener('click', async () => {
        const lines = editor.lines();
        if (!lines.length) return setError(err, 'Add at least one line with a description.');
        const updated = await runAction(save, () => api.put(`/quotes/${encodeURIComponent(quote.id)}`, { lines }), { busyLabel: 'Saving…', errSlot: err });
        if (updated) goTo(quoteHash(quote.id));
      });

      return h('div', {}, [
        result.aiUnavailable ? aiUnavailableNotice(result.message) : null,
        card([
          muted(quoteDraftSummary(result.summary)),
          muted('Review and edit the drafted lines below — flagged lines need your input. This is saved as a draft; sending it is a separate, explicit step.'),
        ], { title: `Draft ${quote.ref}` }),
        guidelineFlagsCard(result.guidelineReview, { jobId: quote.jobId }),
        card([editor.el], { title: 'Lines' }),
        err,
        buttonRow([save, discard]),
      ]);
    }
  };

  return screenEl('New quote', body, { section: 'quotes', backHref: routeToHash({ section: 'quotes' }), backLabel: 'Quotes' });
}

// A "quick add from rate card" control: choosing an item appends a priced line.
export function rateCardAdder(rateItems, editor) {
  if (!rateItems.length) return null;
  const select = makeSelect(
    [{ value: '', label: 'Add from rate card…' }, ...rateItems.map((r) => ({ value: r.id, label: `${r.name} — £${r.unitPrice}/${r.unit}` }))],
    { value: '' },
  );
  select.addEventListener('change', () => {
    const item = rateItems.find((r) => r.id === select.value);
    if (item) editor.add({ description: item.name, quantity: 1, unitPrice: item.unitPrice });
    select.value = '';
  });
  return h('div', { class: 'field' }, [select]);
}

// --- Detail (view + edit lines + status transitions) -----------------------

export function quoteDetailScreen(id) {
  const body = h('div');

  const refresh = () =>
    runAsync(
      body,
      async () => {
        const quote = await api.get(`/quotes/${encodeURIComponent(id)}`);
        const client = await api.get(`/clients/${encodeURIComponent(quote.clientId)}`).catch(() => null);
        return { quote, client };
      },
      ({ quote, client }) => renderView(quote, client),
      { loadingMessage: 'Loading quote…' },
    );

  const renderView = (quote, client) => {
    const wrap = h('div');
    const editBtn = button('Edit lines', {});
    editBtn.addEventListener('click', () => mount(body, renderEdit(quote)));

    const actionErr = errorSlot();
    const transitions = (QUOTE_TRANSITIONS[quote.status] || []).map((next) => {
      const b = button(STATUS_ACTION_LABELS[next] || next, { variant: next === 'sent' ? 'primary' : 'secondary' });
      b.addEventListener('click', async () => {
        await runAction(b, () => api.put(`/quotes/${encodeURIComponent(id)}`, { status: next }), { busyLabel: 'Saving…', errSlot: actionErr });
        refresh();
      });
      return b;
    });

    mount(wrap, h('div', {}, [
      card([
        detailList([
          ['Reference', quote.ref],
          ['Client', client ? client.name : quote.clientId],
          ['Status', statusBadge(quote.status)],
          ['Job', quote.jobId ? linkButton('View job', routeToHash({ section: 'jobs', action: 'view', id: packJobId(quote.clientId, quote.jobId) }), { small: true }) : null],
          ['Updated', shortDate(quote.updatedAt)],
        ]),
      ], { title: quote.ref }),
      card([totalsBlock(quote)], { title: 'Lines' }),
      buttonRow([editBtn]),
      // Download or share the rendered PDF on mobile (Req 8.3, task 6.2).
      pdfDownloadCard({ entity: 'quote', doc: quote }),
      // Confirm-then-push to QuickBooks as an Estimate (Req 7.5, 7.6, NFR6.1).
      quickbooksPushCard({ entity: 'quote', doc: quote, onPushed: () => refresh() }),
      transitions.length ? sectionHeading('Move status') : null,
      transitions.length ? buttonRow(transitions) : muted('This quote is in a final status.'),
      actionErr,
    ]));
    return wrap;
  };

  const renderEdit = (quote) => {
    const editor = linesEditor({ initial: (quote.lines || []).map((l) => ({ description: l.description, quantity: l.quantity, unitPrice: l.unitPrice })) });
    const err = errorSlot();
    const save = button('Save lines', { variant: 'primary' });
    const cancel = button('Cancel', {});
    cancel.addEventListener('click', () => refresh());
    save.addEventListener('click', async () => {
      const lines = editor.lines();
      if (!lines.length) return setError(err, 'Add at least one line with a description.');
      const updated = await runAction(save, () => api.put(`/quotes/${encodeURIComponent(id)}`, { lines }), { busyLabel: 'Saving…', errSlot: err });
      if (updated) refresh();
    });
    return h('div', { class: 'form' }, [sectionHeading('Edit lines'), editor.el, err, buttonRow([save, cancel])]);
  };

  refresh();
  return screenEl('Quote', body, { section: 'quotes', backHref: routeToHash({ section: 'quotes' }), backLabel: 'Quotes' });
}

function truncate(text, max) {
  const s = String(text || '');
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}
